import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customInstance } from "@/lib/api/client";
import type { ContactItem } from "@/features/candidate/components/profile/contact/item";

interface ProfileContactRaw {
  id: string;
  source: string;
  type: "email" | "phone";
  value: string;
  email_type: "personal" | "professional" | null;
  deliverability: string | null;
  carrier: string | null;
  is_valid: boolean | null;
  line_type: string | null;
  revealed_by_name: string | null;
  feedback: "up" | "down" | null;
  created_at: string;
}

interface ContactRevealResponse {
  contacts: ProfileContactRaw[];
  credits_remaining: number;
  already_revealed: boolean;
}

// `source` values the backend writes that represent an org-contributed
// contact (vs. one returned by the PDL / RocketReach / Kickbox enrichment
// waterfall). "manual" = added in-app via Add Email. "upload" = extracted
// from an imported resume during materialization. "ats" = pulled from a
// connected ATS (org data, not a paid reveal). All should show up under
// "Manually Added" and expose the Delete affordance. Keep in sync with
// LOCAL_CONTACT_SOURCES in backend app/models/profile_contact.py.
const LOCAL_CONTACT_SOURCES: ReadonlySet<string> = new Set([
  "manual",
  "upload",
  "ats",
]);

function toContactItem(raw: ProfileContactRaw): ContactItem {
  return {
    id: raw.id,
    type: raw.type,
    value: raw.value,
    emailType: raw.email_type,
    deliverability: raw.deliverability as ContactItem["deliverability"],
    carrier: raw.carrier,
    isValid: raw.is_valid,
    lineType: raw.line_type,
    createdAt: raw.created_at,
    isLocal: LOCAL_CONTACT_SOURCES.has(raw.source),
    source: raw.source,
    contributedByName: raw.revealed_by_name,
    feedback: raw.feedback,
  };
}

export function contactsQueryKey(profileId: string) {
  return ["profile-contacts", profileId] as const;
}

export function useProfileContacts(profileId: string | null) {
  return useQuery({
    queryKey: contactsQueryKey(profileId!),
    queryFn: () =>
      customInstance<ContactRevealResponse>(`/api/profiles/${profileId}/contacts`),
    enabled: !!profileId,
    select: (data) => ({
      ...data,
      contacts: data.contacts.map(toContactItem),
    }),
  });
}

function updateCreditBalance(queryClient: ReturnType<typeof useQueryClient>, creditsRemaining: number) {
  queryClient.setQueryData<{ balance: number }>(
    ["billing", "credits"],
    (old) => old ? { ...old, balance: creditsRemaining } : { balance: creditsRemaining },
  );
}

export function useRevealContact(profileId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      customInstance<ContactRevealResponse>(`/api/profiles/${profileId}/reveal-contact`, {
        method: "POST",
      }),
    onSuccess: (data) => {
      if (profileId) {
        queryClient.setQueryData(contactsQueryKey(profileId), data);
        queryClient.invalidateQueries({ queryKey: contactsQueryKey(profileId) });
      }
      updateCreditBalance(queryClient, data.credits_remaining);
    },
  });
}

export function useRefreshContact(profileId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      customInstance<ContactRevealResponse>(`/api/profiles/${profileId}/refresh-contact`, {
        method: "POST",
      }),
    onSuccess: (data) => {
      if (profileId) {
        queryClient.setQueryData(contactsQueryKey(profileId), data);
        queryClient.invalidateQueries({ queryKey: contactsQueryKey(profileId) });
      }
      updateCreditBalance(queryClient, data.credits_remaining);
    },
  });
}

export function useAddManualContact(profileId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ type, value }: { type: "email" | "phone"; value: string }) =>
      customInstance<ProfileContactRaw>(`/api/profiles/${profileId}/contacts/manual`, {
        method: "POST",
        body: JSON.stringify({ type, value }),
      }),
    onSuccess: () => {
      if (profileId) {
        queryClient.invalidateQueries({ queryKey: contactsQueryKey(profileId) });
      }
    },
  });
}

export function useContactFeedback(profileId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contactId, feedback }: { contactId: string; feedback: "up" | "down" }) =>
      customInstance<ProfileContactRaw>(`/api/contacts/${contactId}/feedback`, {
        method: "POST",
        body: JSON.stringify({ feedback }),
      }),
    // Optimistic update: flip the feedback locally before the request lands so
    // the thumb icon recolors immediately. Roll back on error; reconcile with
    // server truth on settle.
    onMutate: async ({ contactId, feedback }) => {
      if (!profileId) return { previous: undefined };
      const key = contactsQueryKey(profileId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ContactRevealResponse>(key);
      if (previous) {
        queryClient.setQueryData<ContactRevealResponse>(key, {
          ...previous,
          contacts: previous.contacts.map((c) =>
            c.id === contactId ? { ...c, feedback } : c,
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous && profileId) {
        queryClient.setQueryData(contactsQueryKey(profileId), context.previous);
      }
    },
    onSettled: () => {
      if (profileId) {
        queryClient.invalidateQueries({ queryKey: contactsQueryKey(profileId) });
      }
    },
  });
}

export function useDeleteManualContact(profileId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contactId: string) =>
      customInstance<void>(`/api/contacts/${contactId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      if (profileId) {
        queryClient.invalidateQueries({ queryKey: contactsQueryKey(profileId) });
      }
    },
  });
}
